import { Module, Menu, SubMenu } from "../../../models/menu/menu.model";
import { getUrlPathFromComponentPath } from "./routeMapping";
import { getMenuDisplayLabel } from "./displayLabel";

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

export interface BreadcrumbHierarchy {
  module: Module | null;
  menu: Menu | null;
  submenu: SubMenu | null;
}

/**
 * Normalizes and decodes a URL path for comparison
 * Applies the same transformations as URL generation to ensure consistent matching
 */
const normalizePath = (path: string): string => {
  try {
    // First decode URL-encoded characters (e.g., %20 → space, %60 → `)
    let decoded = decodeURIComponent(path);

    // Remove PUBLIC_URL prefix
    decoded = decoded.replace(process.env.PUBLIC_URL || "", "");

    // Remove leading/trailing slashes
    decoded = decoded.replace(/^\/+|\/+$/g, "");

    // Apply the same special character removal as displayNameToUrlPath
    // This ensures paths with special chars match their generated URL equivalents
    return decoded
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "") // Remove special characters (same as URL generation)
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Remove consecutive hyphens
      .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
  } catch (e) {
    // If decoding fails, proceed with the original path
    return path
      .replace(process.env.PUBLIC_URL || "", "")
      .replace(/^\/+|\/+$/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
  }
};

/**
 * Finds the menu hierarchy for a given route/path
 * Returns: { module, menu, submenu }
 */
export const findMenuHierarchy = (
  modules: Module[],
  currentPath: string
): BreadcrumbHierarchy => {
  // Remove PUBLIC_URL prefix, decode URL encoding, and normalize path
  const normalizedCurrentPath = normalizePath(currentPath);

  for (const module of modules) {
    for (const menu of module.menus) {
      // Check if menu has submenus
      if (menu.subMenu && menu.subMenu.length > 0) {
        for (const submenu of menu.subMenu) {
          if (submenu.permissions?.canView) {
            // Generate URL from both route and menuName and normalize them
            const routePath = submenu.route
              ? normalizePath(getUrlPathFromComponentPath(submenu.route))
              : "";

            const menuNamePath = submenu.displayName
              ? normalizePath(getUrlPathFromComponentPath(submenu.displayName))
              : "";

            // Check if either path matches (exact only)
            if (
              routePath === normalizedCurrentPath ||
              menuNamePath === normalizedCurrentPath
            ) {
              return {
                module,
                menu,
                submenu,
              };
            }
          }
        }
      } else {
        // Menu without submenus
        if (menu.permissions?.canView) {
          const routePath = menu.route
            ? normalizePath(getUrlPathFromComponentPath(menu.route))
            : "";

          const menuNamePath = menu.displayName
            ? normalizePath(getUrlPathFromComponentPath(menu.displayName))
            : "";

          if (
            routePath === normalizedCurrentPath ||
            menuNamePath === normalizedCurrentPath
          ) {
            return {
              module,
              menu,
              submenu: null,
            };
          }
        }
      }
    }
  }

  return {
    module: null,
    menu: null,
    submenu: null,
  };
};

/**
 * Generates breadcrumb items for the current route
 * Format: Module Name > Menu Name > Item Name
 */
export const generateBreadcrumbs = (
  modules: Module[],
  currentPath: string
): BreadcrumbItem[] => {
  if (!modules || modules.length === 0) {
    return [];
  }

  const { module, menu, submenu } = findMenuHierarchy(modules, currentPath);

  if (!module || !menu) {
    return [];
  }

  const breadcrumbs: BreadcrumbItem[] = [];

  // Add Module Name
  breadcrumbs.push({
    label: getMenuDisplayLabel(module.displayName),
  });

  // Add Menu Name
  breadcrumbs.push({
    label: getMenuDisplayLabel(menu.displayName),
  });

  // Add SubMenu/Item Name (if exists)
  if (submenu) {
    breadcrumbs.push({
      label: getMenuDisplayLabel(submenu.displayName),
    });
  }

  return breadcrumbs;
};

/**
 * Gets breadcrumb title (last item in breadcrumb)
 */
export const getBreadcrumbTitle = (breadcrumbs: BreadcrumbItem[]): string => {
  if (breadcrumbs.length === 0) return "";
  return breadcrumbs[breadcrumbs.length - 1].label;
};
